/**
 * De onde sai a INTENÇÃO do jogador. Invariante 2: daqui sai direção, NEVER posição.
 *
 * ⚠️ PROVISÓRIO. O controle de toque é MECÂNICA e é decidido na fase 02
 * (ROADMAP), junto com a mecânica nova. O que existe aqui é o mínimo para a
 * fatia vertical da fase 00 andar: arrastar o dedo, e teclado para desenvolver
 * no desktop. Quando a fase 02 decidir o controle, os números saem daqui e vão
 * para `packages/gamedata` (invariante 6).
 *
 * A matemática é pura e escreve em vetor pré-alocado: nada aqui aloca por frame.
 */

/** Direção no plano do mundo. `z` negativo é "para longe da câmera". */
export interface MoveVector {
  x: number;
  z: number;
}

/** Distância de arrasto, em CSS px, até a velocidade máxima. */
const DRAG_FULL_PX = 72;
/** Abaixo disto o dedo está parado; dedo NEVER fica perfeitamente imóvel. */
const DRAG_DEAD_PX = 6;

const KEYS_UP = new Set(['KeyW', 'ArrowUp']);
const KEYS_DOWN = new Set(['KeyS', 'ArrowDown']);
const KEYS_LEFT = new Set(['KeyA', 'ArrowLeft']);
const KEYS_RIGHT = new Set(['KeyD', 'ArrowRight']);

/**
 * Teclas do ímã. Existem para DESENVOLVER e para o E2E dirigir pelo caminho do
 * jogador — o controle de verdade é o botão, que é o que o dedo alcança.
 */
const KEYS_MAGNET = new Set(['Space', 'ShiftLeft', 'ShiftRight']);

function clampToUnitCircle(out: MoveVector): MoveVector {
  const length = Math.hypot(out.x, out.z);
  if (length > 1) {
    out.x /= length;
    out.z /= length;
  }
  return out;
}

/**
 * Arrasto em pixels de tela vira direção de mundo. Tela para cima é `-z` porque
 * a câmera olha a arena de trás para a frente.
 */
export function dragToMove(dx: number, dy: number, out: MoveVector): MoveVector {
  const distance = Math.hypot(dx, dy);
  if (distance <= DRAG_DEAD_PX) {
    out.x = 0;
    out.z = 0;
    return out;
  }
  out.x = dx / DRAG_FULL_PX;
  out.z = dy / DRAG_FULL_PX;
  return clampToUnitCircle(out);
}

/** Teclas seguradas viram direção. Diagonal NEVER anda mais rápido que reto. */
export function keysToMove(pressed: ReadonlySet<string>, out: MoveVector): MoveVector {
  out.x = 0;
  out.z = 0;
  for (const code of pressed) {
    if (KEYS_UP.has(code)) out.z -= 1;
    else if (KEYS_DOWN.has(code)) out.z += 1;
    else if (KEYS_LEFT.has(code)) out.x -= 1;
    else if (KEYS_RIGHT.has(code)) out.x += 1;
  }
  return clampToUnitCircle(out);
}

/** Alguma tecla de ímã segurada. Puro, para poder ser provado sem DOM. */
export function keysToMagnet(pressed: ReadonlySet<string>): boolean {
  for (const code of pressed) {
    if (KEYS_MAGNET.has(code)) return true;
  }
  return false;
}

/**
 * Junta teclado e toque em um vetor só. O dedo manda: quem está no celular NEVER
 * tem teclado, e quem tem os dois quis usar o dedo se está arrastando.
 */
export class IntentSource {
  /** Reutilizado de propósito: NEVER guarde a referência, leia os campos. */
  private readonly current: MoveVector = { x: 0, z: 0 };
  private readonly fromDrag: MoveVector = { x: 0, z: 0 };
  private readonly fromKeys: MoveVector = { x: 0, z: 0 };
  private readonly pressed = new Set<string>();
  private pointerId: number | undefined;
  private originX = 0;
  private originY = 0;
  private attached: HTMLElement | undefined;
  private magnetButton: HTMLElement | undefined;
  /** Ponteiro que está segurando o botão do ímã. Só um: o polegar direito. */
  private magnetPointerId: number | undefined;
  /** Quantas vezes o campo caiu, por causa. Vai ao HUD; ver `release`. */
  private readonly magnetReleaseCounts = { up: 0, cancel: 0, blur: 0 };
  /** Quando o polegar encostou, para medir quanto tempo o campo ficou de pé. */
  private magnetDownAt = 0;
  /**
   * Quem quer saber de cada queda do campo, e NEVER só do total acumulado.
   *
   * O contador serve ao HUD, que responde "quantas vezes"; o ouvinte serve à
   * caixa-preta, que responde "QUANDO, e depois de quanto tempo segurando". As
   * duas perguntas são diferentes: um campo que cai a cada 300 ms é o browser
   * roubando o ponteiro, e um que cai depois de 8 s é o dedo cansando — e o total
   * acumulado NEVER distingue os dois.
   */
  private onRelease:
    | ((cause: 'up' | 'cancel' | 'blur', heldMs: number, pointerId: number, active: number) => void)
    | undefined;
  /**
   * Quem soltou por último, e quando. Alimenta o `sinceReleaseMs` do próximo
   * `pointerdown`: é ele que mede o "solto" do ciclo que estamos caçando.
   */
  private lastReleaseAt = 0;
  private onDown:
    | ((pointerId: number, type: string, active: number, captured: boolean, since: number) => void)
    | undefined;
  /**
   * TODOS os dedos na tela, e NEVER só os que este controle usa.
   *
   * Existe para separar duas hipóteses que o `pointerId` sozinho NEVER separa:
   * "o dedo do ímã pisca" e "o dedo de ANDAR derruba o do ímã". Escutado na
   * janela na fase de CAPTURA, para contar mesmo o toque que outro handler
   * consome antes.
   */
  private readonly activePointers = new Set<number>();

  private readonly onAnyDown = (event: PointerEvent): void => {
    this.activePointers.add(event.pointerId);
  };

  private readonly onAnyUp = (event: PointerEvent): void => {
    this.activePointers.delete(event.pointerId);
  };

  /** Relógio injetável: teste de toque NEVER pode depender de tempo real. */
  constructor(private readonly now: () => number = () => performance.now()) {}

  /** Instala o ouvinte de queda do campo. Um só: quem observa é a caixa-preta. */
  watchMagnetRelease(
    listener: (cause: 'up' | 'cancel' | 'blur', heldMs: number, pointerId: number, active: number) => void,
  ): void {
    this.onRelease = listener;
  }

  /** Instala o ouvinte de PRESSÃO do botão. Sem o par, a queda NEVER conta a história. */
  watchMagnetDown(
    listener: (pointerId: number, type: string, active: number, captured: boolean, since: number) => void,
  ): void {
    this.onDown = listener;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.pointerId !== undefined) return;
    this.pointerId = event.pointerId;
    this.originX = event.clientX;
    this.originY = event.clientY;
    this.attached?.setPointerCapture(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return;
    dragToMove(event.clientX - this.originX, event.clientY - this.originY, this.fromDrag);
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return;
    this.pointerId = undefined;
    this.fromDrag.x = 0;
    this.fromDrag.z = 0;
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    this.pressed.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.pressed.delete(event.code);
  };

  /**
   * Perder o foco tem que zerar tudo: sem isto, trocar de aba com a tecla
   * segurada deixa o jogador andando sozinho para sempre.
   */
  private readonly onBlur = (): void => {
    this.pressed.clear();
    this.pointerId = undefined;
    this.fromDrag.x = 0;
    this.fromDrag.z = 0;
    // O botão também: trocar de aba com o ímã apertado deixaria o campo ligado
    // para sempre, e o jogador voltaria preso a uma âncora que NEVER escolheu.
    this.release('blur');
  };

  private readonly onMagnetDown = (event: PointerEvent): void => {
    if (this.magnetPointerId !== undefined) return;
    this.magnetPointerId = event.pointerId;
    this.magnetDownAt = this.now();
    // A captura pode FALHAR (ponteiro já encerrado, elemento fora do documento), e
    // falhar calada seria perder o fim do toque sem nada dizendo por quê.
    let captured: boolean;
    try {
      this.magnetButton?.setPointerCapture(event.pointerId);
      captured = true;
    } catch {
      captured = false;
    }
    try {
      const since = this.lastReleaseAt === 0 ? 0 : Math.round(this.magnetDownAt - this.lastReleaseAt);
      this.onDown?.(event.pointerId, event.pointerType, this.activePointers.size, captured, since);
    } catch {
      // Observador NEVER derruba controle.
    }
    // Sem isto o polegar no botão também vira arrasto de movimento no canvas.
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * O navegador tentando começar seleção de texto ou menu de contexto EM CIMA do
   * botão segurado.
   *
   * Cancelar é obrigatório, e NEVER é preferência: quando o gesto nativo assume, o
   * browser dispara `pointercancel` e o campo cai sozinho no meio da jogada. É o
   * "segurei e ele meio que soltou" — o defeito NEVER estava na rede.
   */
  private readonly onSteal = (event: Event): void => {
    event.preventDefault();
  };

  /**
   * Solta o campo, e REGISTRA por quê.
   *
   * A contagem NEVER é enfeite: o botão soltando sozinho foi relatado em aparelho
   * real e NEVER se reproduz em emulador. Sem saber QUAL evento derrubou o campo,
   * consertar é chutar — e um chute já falhou. `up` subindo sem o dedo levantar é
   * uma coisa; `cancel` subindo é o browser tomando o ponteiro, que é outra, com
   * outro conserto.
   */
  private release(cause: 'up' | 'cancel' | 'blur'): void {
    const pointerId = this.magnetPointerId;
    if (pointerId === undefined) return;
    this.magnetPointerId = undefined;
    this.magnetReleaseCounts[cause] += 1;
    // Depois do contador: um ouvinte que lance NEVER pode deixar o HUD sem a
    // queda que acabou de acontecer, nem o campo preso ligado.
    const at = this.now();
    const heldMs = Math.max(0, Math.round(at - this.magnetDownAt));
    this.lastReleaseAt = at;
    try {
      this.onRelease?.(cause, heldMs, pointerId, this.activePointers.size);
    } catch {
      // Observador NEVER derruba controle. O ímã é o verbo do jogo.
    }
  }

  /**
   * Fim de toque, escutado na JANELA e NEVER no botão.
   *
   * No elemento, a entrega depende de teste de acerto, e teste de acerto é
   * exatamente o que falha quando o dedo passeia. Na janela, o `pointerup` chega
   * sempre — e é filtrado pelo `pointerId`, então outro dedo NEVER solta este.
   */
  private readonly onWindowPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.magnetPointerId) return;
    this.release('up');
  };

  private readonly onWindowPointerCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.magnetPointerId) return;
    this.release('cancel');
  };

  attach(element: HTMLElement): void {
    this.attached = element;
    element.addEventListener('pointerdown', this.onPointerDown);
    element.addEventListener('pointermove', this.onPointerMove);
    element.addEventListener('pointerup', this.onPointerUp);
    element.addEventListener('pointercancel', this.onPointerUp);
    // Mesma defesa do botão: arrastar o dedo no canvas para ANDAR é, para o
    // browser, indistinguível de arrastar para selecionar. Quando ele decide que
    // é seleção, o ponteiro é cancelado e o robô para no meio do passo.
    element.addEventListener('contextmenu', this.onSteal);
    element.addEventListener('selectstart', this.onSteal);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    // Fase de CAPTURA: conta o dedo mesmo quando outro handler consome o evento.
    window.addEventListener('pointerdown', this.onAnyDown, true);
    window.addEventListener('pointerup', this.onAnyUp, true);
    window.addEventListener('pointercancel', this.onAnyUp, true);
  }

  /** O botão do polegar direito. Elemento próprio: no canvas viraria arrasto. */
  attachMagnet(element: HTMLElement): void {
    this.magnetButton = element;
    element.addEventListener('pointerdown', this.onMagnetDown);
    // O FIM do toque é escutado na janela, e NEVER no botão — ver `onWindowPointerUp`.
    window.addEventListener('pointerup', this.onWindowPointerUp);
    window.addEventListener('pointercancel', this.onWindowPointerCancel);
    // Seleção de texto e menu de contexto NEVER podem nascer sobre o botão: os
    // dois roubam o ponteiro e o browser responde com `pointercancel`.
    element.addEventListener('contextmenu', this.onSteal);
    element.addEventListener('selectstart', this.onSteal);
    // `touchstart` também, e com `passive: false` porque sem isso o browser IGNORA
    // o `preventDefault`. É esta linha que mata o toque-longo do Android antes de
    // ele virar seleção — `pointerdown` NEVER tem poder sobre esse gesto.
    element.addEventListener('touchstart', this.onSteal, { passive: false });
    // ⚠️ `pointerleave` NEVER solta o campo, e isso é o conserto de um bug real.
    //
    // A versão anterior soltava, com o raciocínio de que "o polegar que escorrega
    // para fora do botão SOLTA". O raciocínio é anterior ao `setPointerCapture`
    // que o `onMagnetDown` chama: com a captura, o ponteiro é ENTREGUE a este
    // elemento até levantar, e o `pointerup` chega aqui mesmo que o dedo termine
    // do outro lado da tela.
    //
    // O que sobrava era o defeito: num botão de 88 px coberto pelo polegar, o
    // ponto de toque relatado passeia enquanto o dedo rola. Cada passeio para fora
    // da borda disparava `pointerleave` e derrubava o campo — o jogador segurando
    // o botão e o ímã soltando sozinho, sem nada na tela explicando.
    //
    // O medo original (campo ligado para sempre com o dedo já fora) continua
    // coberto, e por três coisas que NEVER dependem de `pointerleave`:
    // `pointerup` pela captura, `pointercancel` quando o sistema toma o ponteiro,
    // e o `blur` da janela quando a aba perde o foco.
  }

  detach(): void {
    const element = this.attached;
    if (element !== undefined) {
      element.removeEventListener('pointerdown', this.onPointerDown);
      element.removeEventListener('pointermove', this.onPointerMove);
      element.removeEventListener('pointerup', this.onPointerUp);
      element.removeEventListener('pointercancel', this.onPointerUp);
      element.removeEventListener('contextmenu', this.onSteal);
      element.removeEventListener('selectstart', this.onSteal);
    }
    const magnet = this.magnetButton;
    if (magnet !== undefined) {
      magnet.removeEventListener('pointerdown', this.onMagnetDown);
      magnet.removeEventListener('contextmenu', this.onSteal);
      magnet.removeEventListener('selectstart', this.onSteal);
      magnet.removeEventListener('touchstart', this.onSteal);
      window.removeEventListener('pointerup', this.onWindowPointerUp);
      window.removeEventListener('pointercancel', this.onWindowPointerCancel);
    }
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('pointerdown', this.onAnyDown, true);
    window.removeEventListener('pointerup', this.onAnyUp, true);
    window.removeEventListener('pointercancel', this.onAnyUp, true);
    this.activePointers.clear();
    this.attached = undefined;
    this.magnetButton = undefined;
    this.onBlur();
  }

  /** Campo magnético pedido AGORA: botão sob o dedo, ou tecla segurada. */
  get magnet(): boolean {
    return this.magnetPointerId !== undefined || keysToMagnet(this.pressed);
  }

  /**
   * Por que o campo caiu, acumulado na sessão. Referência viva: leia os campos.
   *
   * Existe para diagnosticar em APARELHO, que é onde o defeito vive. O jogador
   * segura o botão, o campo cai, e estes três números dizem qual dos três mundos
   * é o culpado — o dedo, o browser, ou a aba.
   */
  get magnetReleases(): Readonly<{ up: number; cancel: number; blur: number }> {
    return this.magnetReleaseCounts;
  }

  /** Direção deste instante. Caminho quente: NEVER aloca. */
  read(): Readonly<MoveVector> {
    if (this.pointerId !== undefined) {
      this.current.x = this.fromDrag.x;
      this.current.z = this.fromDrag.z;
      return this.current;
    }
    keysToMove(this.pressed, this.fromKeys);
    this.current.x = this.fromKeys.x;
    this.current.z = this.fromKeys.z;
    return this.current;
  }
}
